import {
  constrainToViewport,
  getCanvasContext,
  type StateUpdater,
} from '../../../canvas/core';
import type { PageId } from '../../core';
import { normalizeRotation } from '../../core';
import { getMaxEffectiveWidth } from './helpers';
import type {
  RotationAnimationState,
  RotationDeps,
  RotationSlice,
} from './types';

export const setRotationAnimation =
  <Root extends RotationSlice>(
    pageId: PageId,
    animation: Partial<RotationAnimationState>
  ): StateUpdater<Root> =>
  state => ({
    ...state,
    rotation: {
      ...state.rotation,
      animations: {
        ...state.rotation.animations,
        [pageId]: {
          ...state.rotation.animations[pageId],
          ...animation,
        },
      },
    },
  });

export const setVisualRotation =
  <Root extends RotationSlice>(
    pageId: PageId,
    rotation: number
  ): StateUpdater<Root> =>
  state => ({
    ...state,
    rotation: {
      ...state.rotation,
      animations: {
        ...state.rotation.animations,
        [pageId]: {
          ...state.rotation.animations[pageId],
          visualRotation: normalizeRotation(rotation),
        },
      },
    },
  });

export const setRotationValue =
  <Root extends RotationSlice>(
    pageId: PageId,
    rotation: number
  ): StateUpdater<Root> =>
  state => ({
    ...state,
    rotation: {
      ...state.rotation,
      values: {
        ...state.rotation.values,
        [pageId]: normalizeRotation(rotation),
      },
    },
  });

export const commitFinalRotation =
  <Root extends RotationSlice>(
    pageId: PageId,
    rotation: number
  ): StateUpdater<Root> =>
  state => {
    const { [pageId]: _, ...remainingAnimations } = state.rotation.animations;

    return {
      ...state,
      rotation: {
        ...state.rotation,
        values: {
          ...state.rotation.values,
          [pageId]: normalizeRotation(rotation),
        },
        animations: remainingAnimations,
      },
    };
  };

/**
 * Sets the target rotation and atomically adjusts the camera so the
 * viewport center stays fixed relative to the document center line.
 *
 * When the layout width changes (portrait ↔ landscape), this updater:
 * 1. Sets the animation targetRotation
 * 2. Shifts translateX to compensate for the content-width delta
 * 3. Updates canvas.dimensions to the new layout width
 * 4. Clamps via constrainToViewport (layout-derived, not stale getBBox)
 */
export const setTargetWithCameraAdjustment =
  <Root extends RotationDeps & RotationSlice>(
    pageId: PageId,
    targetRotation: number,
    widthBefore: number
  ): StateUpdater<Root> =>
  state => {
    const withTarget = setRotationAnimation<Root>(pageId, {
      targetRotation,
    })(state);

    const widthAfter = getMaxEffectiveWidth(withTarget);
    const widthDelta = widthAfter - widthBefore;

    if (widthDelta === 0) return withTarget;

    const { zoom, translateX } = withTarget.canvas.transform;
    const adjustedTranslateX = translateX - (widthDelta * zoom) / 2;

    const newDimensions = {
      ...withTarget.canvas.dimensions,
      width: widthAfter,
    };

    const ctx = getCanvasContext({
      ...withTarget.canvas,
      dimensions: newDimensions,
    });

    return {
      ...withTarget,
      canvas: {
        ...withTarget.canvas,
        dimensions: newDimensions,
        transform: constrainToViewport(ctx)({
          ...withTarget.canvas.transform,
          translateX: adjustedTranslateX,
        }),
      },
    };
  };
